import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/authContext';
import { apiService } from '../services/apiService';
import { portalService } from '../services/portalService';

const MyExams = () => {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        setLoading(true);
        setError('');
        const student = await portalService.getCurrentStudent(user.id);
        const [assessmentData, gradeData] = await Promise.all([
          apiService.get(`assessments/student/${student._id}`),
          apiService.get(`grades/student/${student._id}`),
        ]);
        setAssessments(assessmentData || []);
        setGrades(gradeData || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load your assessments.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadAssessments();
    }
  }, [user]);

  const gradedByAssessment = grades.reduce((accumulator, grade) => {
    accumulator[grade.assessmentId] = grade;
    return accumulator;
  }, {});

  const upcoming = assessments.filter(item => !gradedByAssessment[item._id]);
  const graded = assessments.filter(item => gradedByAssessment[item._id]);

  if (loading) return <LoadingSpinner message="Loading your assessments..." />;

  return (
    <div className="page-container">
      <div className="page-header"><h1>My Assessments</h1></div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
        <h2>Upcoming ({upcoming.length})</h2>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr><th>Assessment</th><th>Module</th><th>Type</th><th>Date</th><th>Total Marks</th></tr>
            </thead>
            <tbody>
              {upcoming.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-data-message" style={{ textAlign: 'center', padding: '2rem' }}>
                    No upcoming assessments found. Check back later or contact your module teacher.
                  </td>
                </tr>
              ) : upcoming.map(item => (
                <tr key={item._id}>
                  <td><strong>{item.title}</strong></td>
                  <td>{item.moduleName || item.subjectName}</td>
                  <td>{item.type}</td>
                  <td>{new Date(item.date).toLocaleDateString()}</td>
                  <td>{item.totalMarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="dashboard-section">
        <h2>Graded ({graded.length})</h2>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr><th>Assessment</th><th>Module</th><th>Marks</th><th>%</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {graded.length === 0 ? (
                <tr><td colSpan={5} className="no-data-message">No graded assessments yet.</td></tr>
              ) : graded.map(item => {
                const grade = gradedByAssessment[item._id];
                return (
                  <tr key={item._id}>
                    <td><strong>{item.title}</strong></td>
                    <td>{item.moduleName || item.subjectName}</td>
                    <td>{grade.marksObtained} / {grade.totalMarks}</td>
                    <td>{grade.percentage ?? 0}%</td>
                    <td>{grade.remarks || 'No remarks'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyExams;
